import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Empty,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  AIProvider,
  deleteProvider,
  listProviders,
  providerError,
  setPrimaryProvider,
  updateProvider,
} from './api';
import ProviderEditor, { providerKinds } from './ProviderEditor';
import './styles.css';

const AIProviders: React.FC = () => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState<AIProvider | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AIProvider | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setProviders(await listProviders());
    } catch (reason) {
      setError(providerError(reason));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const changeRouting = async (provider: AIProvider, action: string) => {
    setBusy(true);
    setError('');
    try {
      if (action === 'primary') await setPrimaryProvider(provider.id, true);
      else if (action === 'fallback')
        await setPrimaryProvider(provider.id, false);
      else await updateProvider(provider.id, { enabled: !provider.enabled });
      await load();
    } catch (reason) {
      setError(providerError(reason));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    setDeleteError('');
    try {
      await deleteProvider(deleting.id);
      setDeleting(null);
      await load();
    } catch (reason) {
      setDeleteError(providerError(reason));
    } finally {
      setBusy(false);
    }
  };
  const primary = providers.find(item => item.is_active);
  const filtered = providers.filter(item =>
    `${item.name} ${item.kind} ${item.model} ${item.base_url}`
      .toLowerCase()
      .includes(query.trim().toLowerCase()),
  );
  return (
    <section className="ai-providers" aria-label="AI provider management">
      <header className="ai-providers__heading">
        <div>
          <Typography.Title level={2}>AI Providers</Typography.Title>
          <Typography.Paragraph type="secondary">
            Manage the models and connections used by Market Explorer and
            assessments.
          </Typography.Paragraph>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined aria-hidden="true" />}
          onClick={() => setEditor('new')}
          disabled={busy}
        >
          Add provider
        </Button>
      </header>
      {error && (
        <Alert
          type="error"
          showIcon
          title="Provider request failed"
          description={error}
          action={
            <Button onClick={() => void load()} disabled={busy}>
              Retry
            </Button>
          }
        />
      )}
      <div className="ai-providers__toolbar">
        <Input
          aria-label="Search providers"
          placeholder="Search providers or models"
          prefix={<SearchOutlined />}
          allowClear
          value={query}
          onChange={event => setQuery(event.target.value)}
          style={{ maxWidth: 330 }}
        />
        <Space>
          <Typography.Text type="secondary">
            {primary ? `Primary: ${primary.name}` : 'No primary selected'}
          </Typography.Text>
          <Tooltip title="The primary is tried first, followed by enabled providers in priority order. Features that request a specific model only use providers serving that model.">
            <InfoCircleOutlined
              tabIndex={0}
              aria-label="About provider routing"
            />
          </Tooltip>
          <Button
            aria-label="Refresh providers"
            icon={<ReloadOutlined />}
            onClick={() => void load()}
            disabled={busy}
            loading={loading}
          />
        </Space>
      </div>
      <Table<AIProvider>
        rowKey="id"
        loading={loading}
        dataSource={filtered}
        pagination={false}
        scroll={{ x: 760 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                query ? 'No matching providers' : 'No AI providers configured'
              }
            />
          ),
        }}
        columns={[
          {
            title: 'Provider',
            key: 'provider',
            render: (_, item) => (
              <div>
                <Typography.Text strong>{item.name}</Typography.Text>
                <div className="ai-providers__secondary">
                  {providerKinds.find(kind => kind.value === item.kind)
                    ?.label || item.kind}
                </div>
              </div>
            ),
          },
          {
            title: 'Model & connection',
            key: 'model',
            render: (_, item) => (
              <div>
                <Typography.Text>{item.model}</Typography.Text>
                <div className="ai-providers__secondary">{item.base_url}</div>
              </div>
            ),
          },
          {
            title: 'Status',
            key: 'status',
            width: 145,
            render: (_, item) => (
              <div>
                <Tag
                  color={
                    item.is_active ? 'blue' : item.enabled ? 'green' : 'default'
                  }
                >
                  {item.is_active
                    ? 'Primary'
                    : item.enabled && item.bulk_only
                      ? 'Bulk only'
                      : item.enabled
                        ? 'Fallback'
                        : 'Disabled'}
                </Tag>
                {!item.is_active && item.enabled && !item.bulk_only && (
                  <div className="ai-providers__secondary">
                    Priority {item.priority}
                  </div>
                )}
              </div>
            ),
          },
          {
            title: 'API key',
            key: 'key',
            width: 115,
            render: (_, item) => (
              <Typography.Text type="secondary">
                {item.has_api_key ? 'Stored' : 'Not set'}
              </Typography.Text>
            ),
          },
          {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_, item) => (
              <Space size={0}>
                <Button
                  type="link"
                  onClick={() => setEditor(item)}
                  disabled={busy}
                  aria-label={`Edit ${item.name}`}
                >
                  Edit
                </Button>
                <Dropdown
                  trigger={['click']}
                  disabled={busy}
                  menu={{
                    items: [
                      {
                        key: item.is_active ? 'fallback' : 'primary',
                        label: item.is_active
                          ? 'Use as fallback'
                          : 'Make primary',
                        disabled:
                          Boolean(item.bulk_only) ||
                          (!item.enabled && !item.is_active),
                      },
                      {
                        key: 'enabled',
                        label: item.enabled
                          ? 'Disable provider'
                          : 'Enable provider',
                        disabled: item.is_active,
                      },
                      { type: 'divider' },
                      { key: 'delete', label: 'Delete provider', danger: true },
                    ],
                    onClick: ({ key }) => {
                      if (key === 'delete') {
                        setDeleteError('');
                        setDeleting(item);
                      } else void changeRouting(item, key);
                    },
                  }}
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    aria-label={`More actions for ${item.name}`}
                  />
                </Dropdown>
              </Space>
            ),
          },
        ]}
      />
      {editor && (
        <ProviderEditor
          provider={editor === 'new' ? null : editor}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            void load();
          }}
        />
      )}
      <Modal
        open={Boolean(deleting)}
        title={`Delete ${deleting?.name || 'provider'}?`}
        okText="Delete provider"
        okButtonProps={{ danger: true }}
        confirmLoading={busy}
        cancelButtonProps={{ disabled: busy }}
        closable={!busy}
        mask={{ closable: !busy }}
        keyboard={!busy}
        onCancel={() => setDeleting(null)}
        onOk={() => void remove()}
      >
        <p>
          This removes the provider configuration and its stored API key.
          Existing call history is retained.
        </p>
        {deleting?.is_active && (
          <Alert
            type="warning"
            showIcon
            title="This is the primary provider. Future requests will use the remaining enabled providers that match the requested model."
          />
        )}
        {deleteError && <Alert type="error" title={deleteError} />}
      </Modal>
    </section>
  );
};
export default AIProviders;
